import moment from "moment";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import CampusBuildingMultiSelect from "../../../../common/CampusBuildingMultiSelect";
import { useCampusAccessStatus, useStudentCampusBuildings, invalidateCampusAccessQueries } from "../../../../../query/hooks/QueryHooksCampusAccess";
import { GrantCampusAccess, RevokeCampusAccess } from "../../../../../services/StudentsSettingServices";
import { FORM_DATE_FORMAT } from "../../../../../utils/Constants";
import { mapBuildingsToSelect } from "../../../../../utils/CampusBuildingsUtil";
import PermissionsGate from "../../../../../utils/permissionGate";
import { usePermissionGuard } from "../../../../../hooks/usePermissionGuard";
import { GrantInfoHeader, GrantInfoPanel, GrantInfoText, GrantInfoTitle, StatusPill } from "./PersonalTabs.styles";

const CAMPUS_ACCESS_STATUS = {
    NOT_CREATED: "Not Created",
    ACTIVE: "Active",
    REVOKED: "Revoked",
};

/**
 * Student Administration > Students > Personal Info > Campus Access (Edit), added by AP-1321-5.
 * Deliberately separate from the tab-wide `spiedit` the other Personal blocks use, so campus
 * access can be handed out on its own.
 */
const CAMPUS_ACCESS_EDIT_SCOPE = "sastudentspersonalinfocampusaccessedit";

/**
 * Props PermissionsGate clones over a button when the user lacks CAMPUS_ACCESS_EDIT_SCOPE. The
 * title replaces the button's own, so a greyed-out button explains itself on hover rather than
 * looking broken - the permission is off by default for every role until someone ticks it.
 */
const NO_EDIT_PERMISSION_PROPS = {
    disabled: true,
    title: "You do not have permission to grant or revoke campus access. Ask an administrator to enable Student Administration > Students > Personal Info > Campus Access for your role.",
};

/**
 * The `buildings` row a student is put on when they study online. Its option label is the
 * building's name (see mapSingleBuildingToSelect), which is all the frontend has to go on.
 * It configures no Biostar access group - there are no doors to open - so pairing it with a
 * campus is a contradiction rather than a wider grant.
 */
const ONLINE_BUILDING_NAME = "Online";

/**
 * Drops the ValueContainer's left padding - react-select's own 8px, plus the 12px the global
 * .custom-select-box rule in customold.css adds (overriding a style makes react-select emit a
 * class that rule can't match). This is the padding inside the field; the 15px outside it is
 * Bootstrap's column gutter, which the px-0 wrapperClass below removes.
 */
const CAMPUS_SELECT_STYLES = {
    valueContainer: (base) => ({
        ...base,
        paddingLeft: 0,
    }),
};

function CampusAccessBlock() {
    const { id } = useParams();
    const { data: campusAccessData, isLoading: loadingStatus } = useCampusAccessStatus(id);
    const { data: campusBuildingsData, isLoading: loadingBuildings } = useStudentCampusBuildings(id);
    const loading = loadingStatus || loadingBuildings;
    const [actionLoading, setActionLoading] = useState(false);
    const [expiryDate, setExpiryDate] = useState(moment().add(1, "year").toDate());
    const [selectedBuildings, setSelectedBuildings] = useState([]);

    const status = campusAccessData ? campusAccessData.status : "";
    // null while the permissions are still loading, so treat anything but true as no access:
    const canEditCampusAccess = usePermissionGuard({ scopes: [CAMPUS_ACCESS_EDIT_SCOPE] }) === true;
    const fieldsDisabled = status === CAMPUS_ACCESS_STATUS.ACTIVE || !canEditCampusAccess;

    const onlineCombinedWithCampus =
        selectedBuildings.length > 1 &&
        selectedBuildings.some((building) => building.label?.trim().toLowerCase() === ONLINE_BUILDING_NAME.toLowerCase());

    useEffect(() => {
        if (campusAccessData && campusAccessData.status === CAMPUS_ACCESS_STATUS.ACTIVE && campusAccessData.expiryDate) {
            setExpiryDate(moment(campusAccessData.expiryDate).toDate());
        }
    }, [campusAccessData]);

    useEffect(() => {
        setSelectedBuildings(mapBuildingsToSelect(campusBuildingsData));
    }, [campusBuildingsData]);

    const handleError = (err) => {
        setActionLoading(false);
        Swal.fire({
            icon: "error",
            title: "Error",
            text: err?.response?.data?.message || "Something went wrong",
        });
    };

    const handleSuccess = (text) => {
        setActionLoading(false);
        Swal.fire({
            icon: "success",
            title: "Success",
            text,
        });
        invalidateCampusAccessQueries();
    };

    const handleGrantAccess = async () => {
        setActionLoading(true);
        try {
            await GrantCampusAccess({
                student_id: id,
                expiry_date: moment(expiryDate).toISOString(),
                current_status: status,
                building_ids: selectedBuildings.map((building) => building.value),
            });
            handleSuccess("Campus access granted successfully");
        } catch (error) {
            handleError(error);
        }
    };

    const handleRevokeAccess = async () => {
        const confirmation = await Swal.fire({
            icon: "warning",
            title: "Revoke Campus Access",
            text: "Are you sure you want to revoke this student's campus access?",
            showCancelButton: true,
            confirmButtonText: "Yes, revoke access",
        });
        if (confirmation.isConfirmed) {
            setActionLoading(true);
            try {
                await RevokeCampusAccess({ student_id: id });
                handleSuccess("Campus access revoked successfully");
            } catch (error) {
                handleError(error);
            }
        }
    };

    return (
        <div className="my-tickets-info-list Tickets-main-wrap">
            <div className="edit-icon new-card-header">
                <div className="card-header">Campus Access</div>
                {loading && <i className="fas fa-cog fa-spin ml-2"></i>}
            </div>
            <div className="row">
                <div className="col-md-6 col-lg-4">
                    <label htmlFor="ExpiryDate">
                        Expiry Date <span className="text-danger">*</span>
                    </label>
                    <div className="form-group-blk mb-4">
                        <DatePicker
                            selected={expiryDate}
                            onChange={(date) => setExpiryDate(date)}
                            minDate={new Date()}
                            dateFormat={FORM_DATE_FORMAT}
                            className="form-control cursor-pointer"
                            title="Expiry Date"
                            disabled={fieldsDisabled}
                            placeholderText="Select Expiry Date"
                        />
                    </div>
                    <CampusBuildingMultiSelect
                        value={selectedBuildings}
                        onChange={setSelectedBuildings}
                        label="Allowed Campus"
                        name="campusBuildings"
                        col_md={12}
                        col_lg={12}
                        required
                        isDisabled={fieldsDisabled}
                        filterSameCampus={false}
                        isInvalid={onlineCombinedWithCampus}
                        error={
                            onlineCombinedWithCampus
                                ? "Online access can't be combined with a campus. Remove Online access for this student."
                                : ""
                        }
                        selectStyles={CAMPUS_SELECT_STYLES}
                        wrapperClass="px-0"
                    />
                    {/* One gate per button, never both in the same one - PermissionsGate clones a
                        single child to apply errorProps, so two children would crash it: */}
                    {(status === CAMPUS_ACCESS_STATUS.NOT_CREATED || status === CAMPUS_ACCESS_STATUS.REVOKED) && (
                        <PermissionsGate errorProps={NO_EDIT_PERMISSION_PROPS} scopes={[CAMPUS_ACCESS_EDIT_SCOPE]}>
                            <button
                                className="btn btn-save btn-success rtimg-icon-btn"
                                type="button"
                                title="Grant Access"
                                onClick={handleGrantAccess}
                                disabled={loading || actionLoading || !expiryDate || !selectedBuildings.length || onlineCombinedWithCampus}
                            >
                                {actionLoading ? <i className="fas fa-cog fa-spin"></i> : <i className="fal fa-key"></i>}
                                Grant Campus Access
                            </button>
                        </PermissionsGate>
                    )}
                    {status === CAMPUS_ACCESS_STATUS.ACTIVE && (
                        <PermissionsGate errorProps={NO_EDIT_PERMISSION_PROPS} scopes={[CAMPUS_ACCESS_EDIT_SCOPE]}>
                            <button
                                className="btn btn-save btn-danger rtimg-icon-btn"
                                type="button"
                                title="Revoke Access"
                                onClick={handleRevokeAccess}
                                disabled={loading || actionLoading}
                            >
                                {actionLoading ? <i className="fas fa-cog fa-spin"></i> : <i className="fal fa-ban"></i>}
                                Revoke Access
                            </button>
                        </PermissionsGate>
                    )}
                </div>
                <div className="col-md-6 col-lg-8">
                    <GrantInfoPanel>
                        <GrantInfoHeader>
                            <GrantInfoTitle>Access Grant Information</GrantInfoTitle>
                            <StatusPill $status={status || CAMPUS_ACCESS_STATUS.NOT_CREATED}>{status || CAMPUS_ACCESS_STATUS.NOT_CREATED}</StatusPill>
                        </GrantInfoHeader>
                        <GrantInfoText>
                            {campusAccessData && campusAccessData.createdInfo
                                ? campusAccessData.createdInfo
                                : "Pending access grant creation. Grant access by providing an expiry date on the left."}
                        </GrantInfoText>
                    </GrantInfoPanel>
                </div>
            </div>
        </div>
    );
}

export default CampusAccessBlock;
